import './App.css';
import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import Header from './component/Header';
import Footer from './component/Footer';
import ErrorBoundary from './ErrorBoundary';
import NotFound from './NotFound';
import AccessibilityWidget from './component/AccessibilityWidget';



/* ── Lazy-loaded route components ───────────────────────────── */
const Front = lazy(() => import('./component/Front'));
const Contact = lazy(() => import('./component/Contact'));
const Faq = lazy(() => import('./component/Faq'));
const InvestorHome = lazy(() => import('./component/InvestorHome'));
const Formself = lazy(() => import('./component/ContactForm'));
const PrivacyPolicy = lazy(() => import('./component/PrivacyPolicy'));
const FundTransfer = lazy(() => import('./component/FundTransfer'));
const Complaint = lazy(() => import('./component/Complaint'));
const Tnc = lazy(() => import('./component/Tnc'));
const AccessibilityStatement = lazy(() => import('./component/AccessibilityStatement'));

/* ── Loading fallback (shown while chunk downloads) ─────────── */
function PageLoader() {
  return (
    <div
      role="status"
      aria-live="polite"
      aria-label="Page loading"
      style={{
        minHeight: '50vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#475569',
        fontSize: '1rem',
      }}
    >
      Loading…
    </div>
  );
}

/* ── App root ────────────────────────────────────────────────── */
function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AppShell />
      </ErrorBoundary>
    </BrowserRouter>
  );
}

/* ── App shell (layout) ──────────────────────────────────────── */
function AppShell() {
  return (
    <div className="app-shell" data-app-shell="true">
      <a className="skip-link" href="#main-content">
        Skip to main content
      </a>

      <Header />
      <ScrollManager />
      <main id="main-content" className="app-main" tabIndex="-1">
        <Suspense fallback={<PageLoader />}>
          <MainContent />
        </Suspense>
      </main>
      <Footer />
      <AccessibilityWidget />
    </div>
  );
}

/* ── Scroll manager ─────────────────────────────────────────── */
function ScrollManager() {
  const location = useLocation();

  useEffect(() => {
    const updateHeaderOffset = () => {
      const stickyNavbar = document.querySelector('.navbar.fixed-top');
      const headerOffset = stickyNavbar
        ? Math.ceil(stickyNavbar.getBoundingClientRect().height) + 16
        : 16;
      document.documentElement.style.setProperty(
        '--site-header-offset',
        `${headerOffset}px`
      );
      return headerOffset;
    };

    const scrollToTarget = () => {
      const headerOffset = updateHeaderOffset();
      const prefersReducedMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;
      const hash = location.hash
        ? decodeURIComponent(location.hash.slice(1))
        : '';

      if (hash) {
        const targetElement = document.getElementById(hash);
        if (targetElement) {
          const targetTop =
            window.scrollY +
            targetElement.getBoundingClientRect().top -
            headerOffset;
          window.scrollTo({
            top: Math.max(0, targetTop),
            behavior: prefersReducedMotion ? 'auto' : 'smooth',
          });
          if (!targetElement.hasAttribute('tabindex')) {
            targetElement.setAttribute('tabindex', '-1');
          }
          window.setTimeout(() => {
            targetElement.focus({ preventScroll: true });
          }, prefersReducedMotion ? 0 : 260);
          return;
        }
      }

      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      const mainContent = document.getElementById('main-content');
      if (mainContent) {
        mainContent.focus({ preventScroll: true });
      }
    };

    const animationFrameId = window.requestAnimationFrame(() => {
      window.requestAnimationFrame(scrollToTarget);
    });

    window.addEventListener('resize', updateHeaderOffset);
    updateHeaderOffset();

    return () => {
      window.cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', updateHeaderOffset);
    };
  }, [location.pathname, location.hash]);

  return null;
}

/* ── Routes ──────────────────────────────────────────────────── */
function MainContent() {
  return (
    <Routes>
      {/* ── Primary routes (kebab-case canonical) ── */}
      <Route path="/" element={<Front />} />
      <Route path="/contact-us" element={<Contact />} />
      <Route path="/faq" element={<Faq />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/investor-home" element={<InvestorHome />} />
      <Route path="/terms-and-conditions" element={<Tnc />} />
      <Route path="/contact-form" element={<Formself />} />
      <Route path="/fund-transfer" element={<FundTransfer />} />
      <Route path="/investor/complaint" element={<Complaint />} />
      <Route path="/accessibility-statement" element={<AccessibilityStatement />} />

      {/* ── Legacy PascalCase → redirect to kebab-case ── */}
      <Route path="/Contact" element={<Navigate to="/contact-us" replace />} />
      <Route path="/Faq" element={<Navigate to="/faq" replace />} />
      <Route path="/PrivacyPolicy" element={<Navigate to="/privacy-policy" replace />} />
      <Route path="/InvestorHome" element={<Navigate to="/investor-home" replace />} />
      <Route path="/Tnc" element={<Navigate to="/terms-and-conditions" replace />} />
      <Route path="/form" element={<Navigate to="/contact-form" replace />} />
      <Route path="/FundTransfer" element={<Navigate to="/fund-transfer" replace />} />
      <Route path="/Investor/Complaint" element={<Navigate to="/investor/complaint" replace />} />

      {/* ── 404 catch-all ── */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default App;